/* ============================================================
 * <klaviyo-bis> — modern Back in Stock for sold-out variants.
 *
 * Replaces the legacy klaviyo.enable("backinstock") onsite
 * integration, which broke when onsite.js was made async (the
 * inline klaviyo.init() ran before the SDK loaded and threw).
 *
 * This talks to Klaviyo's PUBLIC client-side Back in Stock API
 * directly, using only the public company id. It has NO
 * dependency on any Klaviyo script being loaded or on script
 * load order, so it can't be silently disabled by a perf/async
 * change the way the legacy integration was.
 *
 * Docs: https://developers.klaviyo.com/en/reference/create_client_back_in_stock_subscription
 * ============================================================ */
class KlaviyoBackInStock extends HTMLElement {
  constructor() {
    super();
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onEsc = this.onEsc.bind(this);
  }

  connectedCallback() {
    this.companyId = this.dataset.companyId;
    this.revision = this.dataset.revision || '2024-06-15';
    this.currentVariantId = this.dataset.variantId;
    this.available = this.dataset.available === 'true';

    this.listId = this.dataset.listId; // marketing list for the opt-in (optional)

    this.trigger = this.querySelector('[data-bis-trigger]');
    this.modal = this.querySelector('[data-bis-modal]');
    this.submitBtn = this.querySelector('[data-bis-submit]');
    this.emailInput = this.querySelector('[data-bis-email]');
    this.marketingCheckbox = this.querySelector('[data-bis-marketing]');
    this.message = this.querySelector('[data-bis-message]');
    this.buttons = this.closest('.product-form__buttons');

    if (this.trigger) this.trigger.addEventListener('click', () => this.openModal());
    this.querySelectorAll('[data-bis-close]').forEach((el) =>
      el.addEventListener('click', () => this.closeModal())
    );
    if (this.modal) {
      this.modal.addEventListener('click', (event) => {
        if (event.target === this.modal) this.closeModal();
      });
    }
    if (this.submitBtn) this.submitBtn.addEventListener('click', () => this.onSubmit());
    if (this.emailInput) {
      // This lives inside the product add-to-cart form. Stop Enter from
      // submitting that form; submit the BIS subscription instead.
      this.emailInput.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.onSubmit();
        }
      });
    }

    // Keep in sync with the variant picker. renderProductInfo() in global.js
    // publishes the full variant object (with .available + .id) on change but
    // does NOT re-render this block, so we update ourselves from the event.
    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
      this.unsubscribe = subscribe(window.PUB_SUB_EVENTS.variantChange, this.onVariantChange);
    }

    this.render();
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
    document.removeEventListener('keydown', this.onEsc);
  }

  onVariantChange(event) {
    const variant = event && event.data && event.data.variant;
    if (!variant) return;
    this.currentVariantId = String(variant.id);
    this.available = !!variant.available;
    this.closeModal();
    this.resetForm();
    this.render();
  }

  /* Show the "Notify Me" button (and hide the disabled Sold out button via the
     .jo-bis-active class) only when the selected variant is unavailable. */
  render() {
    const soldOut = !this.available;
    this.classList.toggle('is-active', soldOut);
    if (this.buttons) this.buttons.classList.toggle('jo-bis-active', soldOut);
  }

  onEsc(event) {
    if (event.key === 'Escape') this.closeModal();
  }

  // Match the legacy modal's Roboto Mono look, but load the font only when a
  // shopper actually opens the modal — no render-blocking cost on page load.
  ensureFont() {
    if (KlaviyoBackInStock.fontRequested) return;
    KlaviyoBackInStock.fontRequested = true;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@400;500;700&display=swap';
    document.head.appendChild(link);
  }

  openModal() {
    if (!this.modal) return;
    this.ensureFont();
    this.modal.removeAttribute('hidden');
    document.addEventListener('keydown', this.onEsc);
    requestAnimationFrame(() => {
      this.modal.classList.add('is-open');
      if (this.emailInput) this.emailInput.focus();
    });
  }

  closeModal() {
    if (!this.modal) return;
    this.modal.classList.remove('is-open');
    document.removeEventListener('keydown', this.onEsc);
    setTimeout(() => this.modal.setAttribute('hidden', ''), 200);
  }

  resetForm() {
    this.setState('idle');
    if (this.emailInput) this.emailInput.value = '';
  }

  setState(state, messageText) {
    this.dataset.state = state;
    if (this.message && messageText != null) this.message.textContent = messageText;
    if (state !== 'error' && this.message) this.message.textContent = '';
  }

  async onSubmit() {
    const email = (this.emailInput && this.emailInput.value || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.setState('error', 'Please enter a valid email address.');
      if (this.emailInput) this.emailInput.focus();
      return;
    }
    if (!this.currentVariantId) {
      this.setState('error', 'Something went wrong. Please refresh and try again.');
      return;
    }

    this.setState('loading');

    try {
      const subscribed = await this.subscribeBackInStock(email);
      if (!subscribed) {
        this.setState('error', 'Sorry, that didn’t go through. Please try again.');
        return;
      }

      // Secondary, non-fatal: marketing list opt-in. Only when a list is
      // configured and the shopper left the box checked. A failure here must
      // NOT fail the back-in-stock signup — the restock notification is the
      // primary promise, marketing is a bonus.
      if (this.listId && this.marketingCheckbox && this.marketingCheckbox.checked) {
        await this.subscribeToMarketing(email);
      }

      this.setState('success');
    } catch (error) {
      this.setState('error', 'Network error. Please try again.');
    }
  }

  // Klaviyo public client Back in Stock API. Returns true on success.
  async subscribeBackInStock(email) {
    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: { data: { type: 'profile', attributes: { email: email } } },
          channels: ['EMAIL'],
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              // Shopify composite id format expected by Klaviyo's catalog.
              id: '$shopify:::$default:::' + this.currentVariantId,
            },
          },
        },
      },
    };

    const response = await fetch(
      'https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' +
        encodeURIComponent(this.companyId),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: this.revision },
        body: JSON.stringify(payload),
      }
    );
    // Klaviyo returns 202 Accepted (empty body) on success.
    return response.ok || response.status === 202;
  }

  // Klaviyo public client Subscriptions API — adds the profile to the marketing
  // list WITH email consent. Non-fatal: swallow errors so a marketing-side
  // failure never blocks the back-in-stock signup.
  async subscribeToMarketing(email) {
    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
              },
            },
          },
        },
        relationships: { list: { data: { type: 'list', id: this.listId } } },
      },
    };

    try {
      await fetch(
        'https://a.klaviyo.com/client/subscriptions/?company_id=' +
          encodeURIComponent(this.companyId),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: this.revision },
          body: JSON.stringify(payload),
        }
      );
    } catch (error) {
      /* non-fatal */
    }
  }
}

if (!customElements.get('klaviyo-bis')) {
  customElements.define('klaviyo-bis', KlaviyoBackInStock);
}
